import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import logo from "@/assets/logo_usa.webp";
import { bairrosRio } from "@/data/bairrosRio";
import { useCity } from "@/contexts/CityContext";

const SectionTitle = ({ children, className = "mb-4" }: { children: React.ReactNode; className?: string }) => (
  <p className={`font-serif text-lg font-semibold ${className}`}>{children}</p>
);

const Footer = () => {
  const { isRio } = useCity();
  if (isRio) return <FooterRio />;
  return <FooterBrasilia />;
};

const FooterBrasilia = () => (
  <footer className="bg-foreground text-primary-foreground">
    <div className="container mx-auto px-4 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="mb-4">
             <img src={logo} alt="Dedetizadora USA - Ultra Serviços Ambientais Brasília" className="h-14 w-auto" width={105} height={56} />
          </div>
          <p className="text-sm opacity-70 leading-relaxed">
            Dedetizadora USA — especialistas em controle de pragas em Brasília. Mais de 20 anos de mercado, mais de 50.000 clientes atendidos. Dedetização, desratização, descupinização e limpeza de caixa d'água com garantia de resultado satisfatório ou seu dinheiro de volta.
          </p>
        </div>

        <nav aria-label="Links para serviços de controle de pragas">
          <SectionTitle>Serviços</SectionTitle>
          <ul className="space-y-2 text-sm opacity-70">
            <li><Link to="/servicos/dedetizacao" className="hover:opacity-100 transition-opacity">Dedetização Profissional</Link></li>
            <li><Link to="/servicos/desratizacao" className="hover:opacity-100 transition-opacity">Desratização</Link></li>
            <li><Link to="/servicos/limpeza-caixa-dagua" className="hover:opacity-100 transition-opacity">Limpeza de Caixa D'Água</Link></li>
            <li><Link to="/servicos/descupinizacao" className="hover:opacity-100 transition-opacity">Descupinização</Link></li>
            <li><Link to="/servicos/formigas" className="hover:opacity-100 transition-opacity">Tratamento contra Formigas</Link></li>
            <li><Link to="/servicos/fog" className="hover:opacity-100 transition-opacity">FOG – Fumigação</Link></li>
            <li><Link to="/servicos/sanitizacao" className="hover:opacity-100 transition-opacity">Sanitização</Link></li>
            <li><Link to="/servicos/anti-tracas" className="hover:opacity-100 transition-opacity">Anti-Traças</Link></li>
            <li><Link to="/servicos/caixa-gordura" className="hover:opacity-100 transition-opacity">Caixa de Gordura</Link></li>
            <li><Link to="/servicos/pulgas-carrapatos" className="hover:opacity-100 transition-opacity">Pulgas e Carrapatos</Link></li>
            <li><Link to="/servicos/aranhas-escorpioes" className="hover:opacity-100 transition-opacity">Aranhas e Escorpiões</Link></li>
            <li><Link to="/servicos/afastamento-pombos" className="hover:opacity-100 transition-opacity">Afastamento de Pombos</Link></li>
          </ul>
        </nav>

        <nav aria-label="Links institucionais">
          <SectionTitle>Institucional</SectionTitle>
          <ul className="space-y-2 text-sm opacity-70">
            <li><Link to="/" className="hover:opacity-100 transition-opacity">Home</Link></li>
            <li><Link to="/diferencial" className="hover:opacity-100 transition-opacity">Diferencial USA</Link></li>
            <li><Link to="/quem-somos" className="hover:opacity-100 transition-opacity">Quem Somos</Link></li>
            <li><Link to="/contato" className="hover:opacity-100 transition-opacity">Contato</Link></li>
            <li><Link to="/brasilia" className="hover:opacity-100 transition-opacity">Atendimento por Bairro</Link></li>
          </ul>
          <div className="mt-6">
            <SectionTitle className="mb-3">Áreas de Atendimento</SectionTitle>
            <p className="text-xs opacity-60 leading-relaxed">
              Atendemos todas as 35 regiões administrativas do DF: Plano Piloto (Asa Sul, Asa Norte), Lago Sul, Lago Norte, Sudoeste, Octogonal, Noroeste, Cruzeiro, Taguatinga, Águas Claras, Guará, Ceilândia, Samambaia, Vicente Pires, Park Way, Jardim Botânico, Sobradinho, Sobradinho II, Planaltina, Gama, Santa Maria, Recanto das Emas, Riacho Fundo, Riacho Fundo II, Núcleo Bandeirante, Candangolândia, Brazlândia, São Sebastião, Paranoá, Itapoã, SIA, SCIA/Estrutural, Fercal, Varjão, Sol Nascente/Pôr do Sol, Arniqueira, Arapoanga e Água Quente.
            </p>
          </div>
        </nav>

        <div>
          <SectionTitle>Contato</SectionTitle>
          <ul className="space-y-3 text-sm opacity-70">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 shrink-0" />
              <a href="tel:+5561992938591" className="hover:opacity-100">+55 61 9 9293-8591</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 shrink-0" />
              <a href="mailto:bsb@ultraservicos.com.br" className="hover:opacity-100">bsb@ultraservicos.com.br</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Brasília – DF</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Seg – Sáb: 7h às 19h</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
      <div className="border-t border-white/10 py-6 text-center text-xs opacity-50">
        <div className="mb-4 flex justify-center">
          <a href="https://www.ohub.com.br/empresa/ultra-servicos-ambientais-ltda-brasalia-df-718411" target="_blank" rel="noopener noreferrer">
            <img src="https://www.ohub.com.br/img/selo-fornecedor-verificado.png" width={252} height={55} alt="Fornecedor Verificado Ohub" className="opacity-90 hover:opacity-100 transition-opacity" loading="lazy" />
          </a>
        </div>
        © {new Date().getFullYear()} Ultra Serviços Ambientais — Dedetizadora USA Brasília. Todos os direitos reservados. CNPJ: 07.532.827/0001-95
      </div>
    </footer>
);

const FooterRio = () => {
  const prefix = "/rio-de-janeiro";
  return (
    <footer className="bg-foreground text-primary-foreground">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-4">
              <img src={logo} alt="Dedetizadora USA - Ultra Serviços Ambientais Rio de Janeiro" className="h-14 w-auto" width={105} height={56} />
            </div>
            <p className="text-sm opacity-70 leading-relaxed">
              Dedetizadora USA — especialistas em controle de pragas no Rio de Janeiro. Mais de 20 anos de mercado, mais de 50.000 clientes atendidos. Dedetização, desratização, descupinização e limpeza de caixa d'água com garantia de resultado satisfatório ou seu dinheiro de volta.
            </p>
          </div>

          <nav aria-label="Links para serviços de controle de pragas">
            <SectionTitle>Serviços</SectionTitle>
            <ul className="space-y-2 text-sm opacity-70">
              <li><Link to={`${prefix}/servicos/dedetizacao`} className="hover:opacity-100 transition-opacity">Dedetização Profissional</Link></li>
              <li><Link to={`${prefix}/servicos/desratizacao`} className="hover:opacity-100 transition-opacity">Desratização</Link></li>
              <li><Link to={`${prefix}/servicos/limpeza-caixa-dagua`} className="hover:opacity-100 transition-opacity">Limpeza de Caixa D'Água</Link></li>
              <li><Link to={`${prefix}/servicos/descupinizacao`} className="hover:opacity-100 transition-opacity">Descupinização</Link></li>
              <li><Link to={`${prefix}/servicos/formigas`} className="hover:opacity-100 transition-opacity">Tratamento contra Formigas</Link></li>
              <li><Link to={`${prefix}/servicos/fog`} className="hover:opacity-100 transition-opacity">FOG – Fumigação</Link></li>
              <li><Link to={`${prefix}/servicos/sanitizacao`} className="hover:opacity-100 transition-opacity">Sanitização</Link></li>
              <li><Link to={`${prefix}/servicos/anti-tracas`} className="hover:opacity-100 transition-opacity">Anti-Traças</Link></li>
              <li><Link to={`${prefix}/servicos/caixa-gordura`} className="hover:opacity-100 transition-opacity">Caixa de Gordura</Link></li>
              <li><Link to={`${prefix}/servicos/pulgas-carrapatos`} className="hover:opacity-100 transition-opacity">Pulgas e Carrapatos</Link></li>
              <li><Link to={`${prefix}/servicos/aranhas-escorpioes`} className="hover:opacity-100 transition-opacity">Aranhas e Escorpiões</Link></li>
              <li><Link to={`${prefix}/servicos/afastamento-pombos`} className="hover:opacity-100 transition-opacity">Afastamento de Pombos</Link></li>
            </ul>
          </nav>

          <nav aria-label="Links institucionais">
            <SectionTitle>Institucional</SectionTitle>
            <ul className="space-y-2 text-sm opacity-70">
              <li><Link to={prefix} className="hover:opacity-100 transition-opacity">Home</Link></li>
              <li><Link to={`${prefix}/diferencial`} className="hover:opacity-100 transition-opacity">Diferencial USA</Link></li>
              <li><Link to={`${prefix}/quem-somos`} className="hover:opacity-100 transition-opacity">Quem Somos</Link></li>
              <li><Link to={`${prefix}/contato`} className="hover:opacity-100 transition-opacity">Contato</Link></li>
              <li><Link to={prefix} className="hover:opacity-100 transition-opacity">Atendimento por Bairro</Link></li>
            </ul>
            <div className="mt-6">
              <SectionTitle className="mb-3">Áreas de Atendimento</SectionTitle>
              <p className="text-xs opacity-60 leading-relaxed">
                Atendemos os principais bairros do Rio de Janeiro: {bairrosRio.map((b) => b.name).join(", ")} e região metropolitana.
              </p>
            </div>
          </nav>

          <div>
            <SectionTitle>Contato</SectionTitle>
            <ul className="space-y-3 text-sm opacity-70">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                <a href="tel:+5521968500754" className="hover:opacity-100">(21) 96850-0754</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                <a href="mailto:hellengoes.usarj@gmail.com" className="hover:opacity-100">hellengoes.usarj@gmail.com</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Travessa Ururai, 111, Madureira — Rio de Janeiro, RJ</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Seg – Sáb: 7h às 19h</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs opacity-50">
        <div className="mb-4 flex justify-center">
          <a href="https://www.ohub.com.br/empresa/ultra-servicos-ambientais-ltda-brasalia-df-718411" target="_blank" rel="noopener noreferrer">
            <img src="https://www.ohub.com.br/img/selo-fornecedor-verificado.png" width={252} height={55} alt="Fornecedor Verificado Ohub" className="opacity-90 hover:opacity-100 transition-opacity" loading="lazy" />
          </a>
        </div>
        © {new Date().getFullYear()} Ultra Serviços Ambientais — Dedetizadora USA Rio de Janeiro. Todos os direitos reservados. CNPJ: 07.532.827/0001-95
      </div>
    </footer>
  );
};

export default Footer;
